import { Injectable, inject } from '@angular/core';

import { SessionService } from './session.service';
import { StorageService } from './storage.service';
import { userPrefKey } from './storage-keys';
import { userPreference } from './user-preference';

export interface RecentSearchItem {
  id: string;
  type: 'project' | 'nav' | 'sub-action' | 'active-request' | 'pending-approval';
  label: string;
  subtitle?: string;
  icon?: string;
  color?: string;
  route: string;
  queryParams?: Record<string, string>;
  visitedAt: number;
}

const MAX_ITEMS = 10;

const NONE: readonly RecentSearchItem[] = Object.freeze([]);

/**
 * What the user last opened from the search palette. Scoped by account — a recent-items list
 * names projects and requests, so an unscoped one told whoever logged in next what the previous
 * person had been looking at. It reads as empty until the session arrives.
 */
@Injectable({
  providedIn: 'root',
})
export class RecentItemsService {
  private readonly storage = inject(StorageService);
  private readonly session = inject(SessionService);

  private readonly recent = userPreference<readonly RecentSearchItem[]>({
    userId: this.session.userId,
    read: (userId) => this.readFromStorage(userId),
    write: (userId, items) => this.storage.setJson(userPrefKey.recentSearches(userId), items),
    empty: NONE,
  });

  readonly items = this.recent.value;

  record(item: Omit<RecentSearchItem, 'visitedAt'>): void {
    const deduped = this.items().filter((i) => i.id !== item.id);
    this.recent.set([{ ...item, visitedAt: Date.now() }, ...deduped].slice(0, MAX_ITEMS));
  }

  clear(): void {
    this.recent.set([]);
  }

  private readFromStorage(userId: string): readonly RecentSearchItem[] {
    const parsed = this.storage.getJson<unknown>(userPrefKey.recentSearches(userId), NONE);
    return Array.isArray(parsed) ? (parsed as RecentSearchItem[]) : NONE;
  }
}
